import { useState, useEffect } from 'react';
import { HowItWorks, Loading, RATINGS, api, useToast } from './_barrel.js';


        // =======================================================================
        // Business module — list, detail, dialogs
        // =======================================================================

        function BusinessesView({ token, setView, setSelectedBusiness, openDialog }) {
            const [businesses, setBusinesses] = useState(null);
            const [error, setError] = useState(null);
            const [refresh, setRefresh] = useState(0);
            const [showArchived, setShowArchived] = useState(false);
            const toast = useToast();

            useEffect(() => {
                (async () => {
                    try {
                        const qs = showArchived ? '?include_archived=true' : '';
                        const r = await api(`/api/v1/businesses${qs}`, {}, token);
                        setBusinesses(r);
                    } catch (e) { setError(e.message); }
                })();
            }, [token, refresh, showArchived]);

            const quickArchive = async (e, biz) => {
                e.stopPropagation();
                const reason = prompt(`Archive "${biz.display_name || biz.legal_name}"? Enter a brief reason:`, "");
                if (!reason || !reason.trim()) return;
                try {
                    await api(`/api/v1/businesses/${biz.id}`, {
                        method: 'DELETE',
                        body: JSON.stringify({ deletion_reason: reason.trim() }),
                    }, token);
                    toast(`Archived ${biz.display_name || biz.legal_name}`);
                    setRefresh(r => r+1);
                } catch (err) { setError(err.message); }
            };

            const quickRestore = async (e, biz) => {
                e.stopPropagation();
                if (!confirm(`Restore "${biz.display_name || biz.legal_name}" from archive?`)) return;
                try {
                    await api(`/api/v1/businesses/${biz.id}/restore`, { method: 'POST' }, token);
                    toast(`Restored ${biz.display_name || biz.legal_name}`);
                    setRefresh(r => r+1);
                } catch (err) { setError(err.message); }
            };

            if (error) return <div className="max-w-5xl mx-auto px-6 py-8 text-[var(--red)]">{error}</div>;
            if (!businesses) return <Loading />;

            return (
                <div className="max-w-7xl mx-auto px-6 py-10">
                    <div className="flex justify-between items-start mb-8">
                        <div>
                            <div className="font-mono text-xs uppercase tracking-widest text-muted mb-2">Module</div>
                            <h2 className="font-display text-5xl gold mb-2">Businesses</h2>
                            <div className="text-muted font-mono text-sm">
                                Corporate entities under investigation or monitoring
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <label className="flex items-center gap-2 text-xs font-mono text-muted cursor-pointer">
                                <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
                                Show archived
                            </label>
                            <button className="btn-gold" onClick={() => openDialog('new-business', { onSuccess: () => setRefresh(r => r+1) })}>
                                + Add Business
                            </button>
                        </div>
                    </div>

                    <HowItWorks id="businesses" title="How the Businesses desk works"
                        intro={'The Businesses desk runs due diligence on corporate entities — UK via <b>Companies House</b> and worldwide via the <b>GLEIF</b> register. You add a company; the platform pulls the filings, screens exposure and media, and drafts a single risk verdict.'}
                        steps={[
                            '<b>+ Add Business</b> — look up a UK company via <b>Companies House</b>, or switch to <b>Worldwide</b> to find any entity in the global <b>GLEIF</b> register.',
                            'Open the record — it pulls <b>officers, filings, breach exposure, sanctions and adverse-media / Google-news mentions</b>, and computes an exposure grade (A–F) alongside the risk band.',
                            '<b>🤖 Refresh &amp; re-verdict</b> — re-runs Google-news, exposure screening and the synthesis in one click, then redraws the verdict.',
                            '<b>Review</b> — the synthesis panel gives a single so-what verdict; the mentions list shows what was found, including re-classified hits.',
                            '<b>Sign off</b> to lock the business assessment, then export the diligence report.',
                            'A <b>⟳ REFRESH DUE</b> flag appears when an assessment is older than 30 days.',
                        ]} />
                    {businesses.items.length === 0 ? (
                        <div className="panel p-12 text-center text-muted">
                            No businesses on file yet. Click "Add Business" to look one up via Companies House.
                        </div>
                    ) : (
                        <div className="panel">
                            <div className="panel-header">
                                <div className="font-display text-xl gold">{showArchived ? 'All' : 'Active'} ({businesses.total})</div>
                            </div>
                            {businesses.items.map(b => (
                                <div key={b.id}
                                     className="table-row px-6 py-4 cursor-pointer"
                                     onClick={() => { setSelectedBusiness(b.id); setView('business-detail'); }}>
                                    <div className="flex justify-between items-start gap-4">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <div className="font-display text-lg gold">{b.display_name || b.legal_name}</div>
                                                {(() => {
                                                    const hasFullCHRecord = b.jurisdiction === 'GB' && !!b.company_number && !!b.ch_last_refreshed_at;
                                                    return (
                                                        <span className="font-mono text-xs uppercase tracking-widest px-2 py-0.5" style={{
                                                            color: hasFullCHRecord ? 'var(--green)' : 'var(--amber)',
                                                            border: `1px solid ${hasFullCHRecord ? 'var(--green)' : 'var(--amber)'}`,
                                                            borderRadius: '4px',
                                                        }}>
                                                            {hasFullCHRecord ? 'UK — full CH record' : 'US/Intl — name & status only'}
                                                        </span>
                                                    );
                                                })()}
                                            </div>
                                            <div className="text-xs text-muted font-mono">{b.legal_name}</div>
                                            <div className="text-xs text-muted font-mono mt-1">
                                                {b.company_number && `${b.company_number} · `}
                                                {b.jurisdiction}
                                                {b.company_status && ` · ${b.company_status}`}
                                                {b.incorporated_at && ` · incorporated ${b.incorporated_at}`}
                                            </div>
                                            {b.client_tag && (
                                                <div className="text-xs font-mono mt-1" style={{color:'var(--gold)'}}>
                                                    {b.client_tag}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex flex-col items-end gap-2" style={{minWidth: '100px'}}>
                                            <div className="flex items-center gap-2">
                                                {b.risk_band && (
                                                    <span className="label-tag" title="Current risk band from the latest signed assessment"
                                                          style={{color: RATINGS.hex(b.risk_band), borderColor: RATINGS.hex(b.risk_band)}}>
                                                        {RATINGS.label(b.risk_band)}
                                                    </span>
                                                )}
                                                {b.exposure_grade && (
                                                    <span className="label-tag" title="Digital-exposure posture grade (A best … F worst)"
                                                          style={{color: RATINGS.gradeHex(b.exposure_grade), borderColor: RATINGS.gradeHex(b.exposure_grade)}}>
                                                        Exp {b.exposure_grade}
                                                    </span>
                                                )}
                                            </div>
                                            {b.deleted_at && (
                                                <span className="label-tag" style={{color:'var(--text-dim)'}}>Archived</span>
                                            )}
                                            {b.deleted_at ? (
                                                <button className="text-xs font-mono uppercase tracking-widest text-muted hover:text-[var(--gold)]"
                                                    onClick={(e) => quickRestore(e, b)}>
                                                    Restore
                                                </button>
                                            ) : (
                                                <button className="text-xs font-mono uppercase tracking-widest text-muted hover:text-[var(--red)]"
                                                    onClick={(e) => quickArchive(e, b)}>
                                                    Archive
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

export { BusinessesView };
